import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { auth } from "../lib/api";
import { SrLogo } from "../components/smartreach/Chrome";
import { Loader2, CheckCircle2, ArrowRight, ShieldAlert } from "lucide-react";
import "../smartreach.css";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Welcome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | paid | failed
  const [planName, setPlanName] = useState(null);
  const [err, setErr] = useState(null);
  const sessionId = params.get("sub_session");

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); setErr("Missing checkout session ID."); return; }
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/api/public/subscriptions/status/${sessionId}`);
        if (data.plan_key) setPlanName(data.plan_key);
        if (data.payment_status === "paid" && data.token && data.user) {
          auth.login(data.token, data.user);
          setStatus("paid");
          setTimeout(() => { window.location.href = "/app"; }, 1500);
          return;
        }
      } catch (e) {
        if (attempts > 24) { // ~2 minutes at 5s
          setStatus("failed");
          setErr(e?.response?.data?.detail || "We couldn't confirm your payment. Email hello@smartreach.ai with your receipt and we'll sort it out.");
          return;
        }
      }
      if (!stopped) setTimeout(tick, 5000);
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="sr-root" data-testid="welcome-page"
      style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--sr-bg,#f6f8fc)" }}>
      <nav style={{ borderBottom: "1px solid var(--border,#e5e9f0)", background: "#fff" }}>
        <div className="sr-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 24px" }}>
          <SrLogo />
        </div>
      </nav>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        {status === "polling" && (
          <div style={{ textAlign: "center", maxWidth: 460 }} data-testid="welcome-polling">
            <Loader2 size={40} className="sr-spin" style={{ color: "var(--blue)", margin: "0 auto" }} />
            <h1 style={{ fontSize: 30, marginTop: 22 }}>Confirming your payment…</h1>
            <p className="sr-muted" style={{ marginTop: 12, fontSize: 14 }}>
              This normally takes a few seconds. Don&apos;t close this window — we&apos;ll drop you into your workspace as soon as payment is confirmed.
            </p>
            {planName && <div style={{ marginTop: 16, fontSize: 12, color: "var(--blue)", textTransform: "uppercase", letterSpacing: 2 }}>Plan · {planName.replace(/_/g, " ")}</div>}
          </div>
        )}
        {status === "paid" && (
          <div style={{ textAlign: "center", maxWidth: 460 }} data-testid="welcome-success">
            <CheckCircle2 size={56} style={{ color: "var(--success,#16a34a)", margin: "0 auto" }} />
            <h1 style={{ fontSize: 36, marginTop: 22 }}>Welcome to SmartReachAI.</h1>
            <p className="sr-muted" style={{ marginTop: 12 }}>Your subscription is active. Setting up your workspace…</p>
            <button onClick={() => { window.location.href = "/app"; }}
              data-testid="welcome-continue-btn" className="sr-btn sr-btn-primary"
              style={{ marginTop: 22 }}>
              Go to your workspace <ArrowRight size={16} />
            </button>
          </div>
        )}
        {status === "failed" && (
          <div style={{ textAlign: "center", maxWidth: 460 }} data-testid="welcome-failed">
            <ShieldAlert size={56} style={{ color: "var(--danger,#dc2626)", margin: "0 auto" }} />
            <h1 style={{ fontSize: 30, marginTop: 22 }}>We couldn&apos;t confirm your payment.</h1>
            <p className="sr-muted" style={{ marginTop: 12, fontSize: 14 }}>{err || "Please refresh, or email hello@smartreach.ai with your receipt and we\u2019ll sort it out."}</p>
            <div style={{ marginTop: 22, display: "flex", justifyContent: "center", gap: 12 }}>
              <button onClick={() => navigate("/pricing")} data-testid="welcome-retry-btn" className="sr-btn sr-btn-ghost">
                Back to pricing
              </button>
              <a href="mailto:hello@smartreach.ai" className="sr-btn sr-btn-primary">Email support</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
